import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
} from '@mui/material';
import {
  Brightness4Rounded,
  Brightness7Rounded,
  GridViewRounded,
  MenuRounded,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useThemeMode } from '../../hooks/useThemeMode';
import { useAnalytics } from '../../hooks/useAnalytics';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { toggleTheme } = useThemeMode();
  const { trackClick, trackEvent } = useAnalytics();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    trackClick(`nav-${id}`, 'navigation');
    setMobileMenuAnchor(null);
  };

  const setLanguage = (language: 'pt' | 'en') => {
    const previousLanguage = i18n.language;
    i18n.changeLanguage(language);
    localStorage.setItem('i18nextLng', language);
    trackEvent({
      name: 'language_change',
      properties: { from: previousLanguage, to: language },
    });
  };

  const handleThemeToggle = () => {
    toggleTheme();
    trackEvent({
      name: 'theme_toggle',
      properties: { newTheme: theme.palette.mode === 'light' ? 'dark' : 'light' },
    });
  };

  const navigation = [
    { id: 'about', label: t('header.about'), code: '01' },
    { id: 'skills', label: t('header.skills'), code: '02' },
    { id: 'projects', label: t('header.projects'), code: '03' },
    { id: 'contact', label: t('header.contact'), code: '04' },
  ];

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        zIndex: 1200,
        color: 'var(--game-ink)',
        background: theme.palette.mode === 'dark' ? 'rgba(7, 17, 31, 0.86)' : 'rgba(247, 251, 255, 0.88)',
        borderBottom: '1px solid var(--game-line)',
        backdropFilter: 'blur(24px) saturate(150%)',
      }}
    >
      <Toolbar sx={{ minHeight: '72px !important', width: 'min(1480px, 100%)', mx: 'auto', px: { xs: 1.5, md: 3 } }}>
        <Box
          component="button"
          onClick={() => scrollToSection('about')}
          aria-label="Ângelo Castro — início"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            p: 0,
            border: 0,
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '12px',
              background: 'linear-gradient(145deg, #3c83f5, #7658df)',
              boxShadow: '0 9px 22px rgba(47,115,238,.25)',
              color: '#fff',
            }}
          >
            <GridViewRounded fontSize="small" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.05 }}>
            <Box component="strong" sx={{ fontFamily: 'Space Grotesk, Inter, sans-serif', fontSize: '.94rem' }}>
              ANGELO.DEV
            </Box>
            <Box component="span" sx={{ color: 'var(--game-muted)', fontSize: '.56rem', letterSpacing: '.13em' }}>
              PORTFÓLIO
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.4, ml: 5 }}>
          {navigation.map((item) => (
            <Button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              sx={{
                gap: 0.8,
                px: 1.4,
                color: 'var(--game-muted)',
                fontSize: '.74rem',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { color: 'var(--game-blue)', background: 'rgba(47,115,238,.07)' },
              }}
            >
              <Box component="span" sx={{ color: 'var(--game-blue)', fontSize: '.55rem', fontWeight: 900 }}>
                {item.code}
              </Box>
              {item.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <Box sx={{ display: 'flex', p: '3px', border: '1px solid var(--game-line)', borderRadius: '11px' }}>
            {(['pt', 'en'] as const).map((language) => {
              const active = i18n.language.startsWith(language);
              return (
                <Button
                  key={language}
                  onClick={() => setLanguage(language)}
                  aria-pressed={active}
                  sx={{
                    minWidth: 34,
                    px: 0.7,
                    py: 0.5,
                    borderRadius: '8px',
                    color: active ? '#fff' : 'var(--game-muted)',
                    background: active ? 'var(--game-blue)' : 'transparent',
                    fontSize: '.62rem',
                    fontWeight: 900,
                    '&:hover': { background: active ? 'var(--game-blue)' : 'rgba(47,115,238,.07)' },
                  }}
                >
                  {language.toUpperCase()}
                </Button>
              );
            })}
          </Box>

          <Tooltip title={theme.palette.mode === 'dark' ? t('theme.toggleLight') : t('theme.toggleDark')}>
            <IconButton onClick={handleThemeToggle} size="small" sx={{ color: 'var(--game-muted)' }}>
              {theme.palette.mode === 'dark' ? <Brightness7Rounded /> : <Brightness4Rounded />}
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={(event) => setMobileMenuAnchor(event.currentTarget)}
            size="small"
            sx={{ display: { md: 'none' }, color: 'var(--game-muted)' }}
            aria-label="Menu"
          >
            <MenuRounded />
          </IconButton>
        </Box>

        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={() => setMobileMenuAnchor(null)}
          PaperProps={{ sx: { mt: 1, minWidth: 210, borderRadius: 3, border: '1px solid var(--game-line)' } }}
        >
          {navigation.map((item) => (
            <MenuItem key={item.id} onClick={() => scrollToSection(item.id)} sx={{ gap: 1.2 }}>
              <Box component="span" sx={{ color: 'var(--game-blue)', fontSize: '.63rem', fontWeight: 900 }}>
                {item.code}
              </Box>
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
